import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  IconButton,
  Tooltip,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNotify, useRedirect } from 'react-admin';
import { httpClient, publicBase, adminBase } from './dataProvider';
import TextbookViewer from './components/TextbookViewer';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const d = new Date(dateString);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const H = String(d.getHours());
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${H}:${m}`;
};

const steps = ['1. Textbook', '2. Template', '3. Review & Generate'];

const ExamWizard = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Step 1: textbooks (from course_material_content, status=final, has_html=true)
  const [textbooks, setTextbooks] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [selectedCourseTitle, setSelectedCourseTitle] = useState('');

  // Step 2: templates
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplError, setTplError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Save-as
  const [saveAs, setSaveAs] = useState('draft');

  // Generation state
  const [generating, setGenerating] = useState(false);

  // HTML viewer dialog state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerHtml, setViewerHtml] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');

  const openHtmlViewer = async (row) => {
    if (!row) return;
    // Choose material id that has HTML if available
    const materialId = row.html_material_id || row.material_id;
    if (!materialId) return;
    setViewerLoading(true);
    setViewerTitle(row.title || 'Textbook');
    try {
      const base = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';
      const res = await fetch(`${base}/api/admin/course_material_content/${materialId}/html`, {
        headers: {
          'X-User-Id': localStorage.getItem('userId') || 'cacecc53-eb64-4dce-a8d3-6a97b2fa9fa4'
        }
      });
      if (!res.ok) {
        throw new Error('Failed to load textbook content');
      }
      const html = await res.text();
      setViewerHtml(html);
      setViewerOpen(true);
    } catch (e) {
      console.error('Failed loading textbook HTML', e);
      notify(e.message || 'Failed to load textbook content', { type: 'error' });
    } finally {
      setViewerLoading(false);
    }
  };

  const closeHtmlViewer = () => {
    setViewerOpen(false);
    setViewerHtml('');
  };

  // Load textbooks
  useEffect(() => {
    setLoading(true);
    const url = `${adminBase}/course_material_content/?_start=0&_end=200&_sort=created_at&_order=DESC`;
    httpClient(url)
      .then(({ json }) => {
        const list = Array.isArray(json) ? json : [];
        // Filter has_html=true
        const filtered = list.filter((r) => r.has_html);
        setTextbooks(filtered);
      })
      .catch((e) => {
        console.error('Failed to load textbooks', e);
        const msg = e?.body?.detail || e?.message || 'Failed to load textbooks';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, []);

  const canProceedFromStep = (step) => {
    if (step === 0) return !!selectedMaterialId;
    if (step === 1) return !!selectedTemplateId;
    return true;
  };

  const handleNext = () => {
    if (!canProceedFromStep(activeStep)) return;
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  // Load templates when entering Step 2
  useEffect(() => {
    if (activeStep !== 1) return;
    if (templates.length > 0) return;
    setTplLoading(true);
    setTplError(null);
    httpClient(`${adminBase}/prompt_templates/?_start=0&_end=100&_sort=created_at&_order=DESC`)
      .then(({ json }) => {
        setTemplates(Array.isArray(json) ? json : []);
      })
      .catch((e) => {
        console.error('Failed to load templates', e);
        const msg = e?.body?.detail || e?.message || 'Failed to load templates';
        setTplError(msg);
      })
      .finally(() => setTplLoading(false));
  }, [activeStep, templates.length]);

  const renderTextbookSelection = () => (
    <>
      <Alert severity="info">Select a textbook to base the exam on.</Alert>
      <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 420, overflow: 'auto' }}>
        {textbooks.map((row) => (
          <ListItem
            key={row.material_id}
            disablePadding
            sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', mb: 1.5 }}
            secondaryAction={
              <Radio
                edge="end"
                checked={selectedMaterialId === row.material_id}
                onChange={() => { setSelectedMaterialId(row.material_id); setSelectedCourseTitle(row.title || ''); }}
              />
            }
          >
            <ListItemButton onClick={() => { setSelectedMaterialId(row.material_id); setSelectedCourseTitle(row.title || ''); }} selected={selectedMaterialId === row.material_id}>
              <ListItemText
                primary={row.title}
                secondary={
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: 1 }}>{row.description || 'No description'}</span>
                      {row.has_html ? (
                        <Tooltip title="View textbook">
                          <IconButton
                            size="small"
                            aria-label={`View ${row.title}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              openHtmlViewer(row);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic', fontSize: 12 }}>No textbook</span>
                      )}
                    </div>
                    <Typography variant="body2" color="text.secondary" component="div">
                      <strong>Category:</strong> {row.category || 'N/A'}<br />
                      <strong>Complexity:</strong> {row.complexity || 'N/A'}<br />
                      {row.updated_at && <><strong>Last Updated:</strong> {formatDate(row.updated_at)}<br /></>}
                      <strong>Status:</strong> {row.status?.toUpperCase() || 'DRAFT'}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  const renderTemplateSelection = () => (
    <>
      {tplLoading && <CircularProgress />}
      {tplError && <Alert severity="error">{tplError}</Alert>}
      <TextField
        select
        label="Prompt Template"
        value={selectedTemplateId}
        onChange={(e) => setSelectedTemplateId(e.target.value)}
        helperText="Choose a prompt template for exam generation"
        fullWidth
      >
        {templates.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name}{t.description ? ` — ${t.description}` : ''}
          </MenuItem>
        ))}
      </TextField>
    </>
  );

  const renderReview = () => (
    <>
      <Typography variant="subtitle1">Review</Typography>
      <div><strong>Textbook:</strong> {selectedCourseTitle || '-'}</div>
      <div><strong>Prompt Template:</strong> {templates.find((t) => t.id === selectedTemplateId)?.name || '-'}</div>
      <TextField
        select
        label="Save As"
        value={saveAs}
        onChange={(e) => setSaveAs(e.target.value)}
        fullWidth
        sx={{ mt: 2 }}
      >
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="final">Final</MenuItem>
      </TextField>
    </>
  );

  const handleGenerate = async () => {
    try {
      if (!selectedMaterialId) {
        notify('Please select a textbook', { type: 'warning' });
        return;
      }
      if (!selectedTemplateId) {
        notify('Please select a prompt template', { type: 'warning' });
        return;
      }

      setGenerating(true);
      notify('Please wait 1 to 3 minutes while generating the exam...', { type: 'info' });

      const payload = {
        course_material_id: selectedMaterialId,
        prompt_template_id: selectedTemplateId,
        params: {},
        save_as: saveAs,
      };

      const response = await httpClient(`${publicBase}/exams/generate_from_textbook`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response && response.json && response.json.id) {
        notify('Exam generation started successfully!', { type: 'success' });
        redirect('/course_material_content');
      } else {
        notify('Exam generation request sent.', { type: 'info' });
      }
    } catch (e) {
      let msg = e?.body?.detail || e?.message || 'Failed to start exam generation';
      if (typeof msg !== 'string') msg = 'Failed to start exam generation';
      notify(msg, { type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Card sx={{ m: 2 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Exam Wizard
        </Typography>
        {/* HTML Textbook Viewer Dialog */}
        <TextbookViewer
          open={viewerOpen}
          onClose={closeHtmlViewer}
          title={viewerTitle}
          htmlContent={viewerHtml}
          loading={viewerLoading}
        />
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {activeStep === 0 && renderTextbookSelection()}
          {activeStep === 1 && renderTemplateSelection()}
          {activeStep === 2 && renderReview()}
        </Stack>
      </CardContent>
      <CardActions>
        <Button onClick={handleBack} disabled={activeStep === 0}>Back</Button>
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={handleNext} disabled={!canProceedFromStep(activeStep)}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} /> : null}
          >
            {generating ? 'Generating...' : 'Generate'}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default ExamWizard;
